import React, { useState, useContext } from 'react';
import { usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeFieldNameMutation } from 'randmar-api-client';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, useToast } from 'randmarcomps';
import { Loader2 } from 'lucide-react';
import ManufacturerContext from '../../contexts/ManufacturerContext';

interface IProps {
  informationName: string;
  informationCurrentValue?: string;
  label?: string;
  validateFonction: (value: any) => boolean;
  categoryCode: string;
  isFloatInput: boolean;
  children: React.ReactNode;
}

const NUMERIC_VALUE_LABELS = new Set(["MinimumOrder", "LeadTime", "WeeksOfStocking"]);

const EditCategoryWrapper: React.FC<IProps> = ({ informationName, informationCurrentValue, label, validateFonction, categoryCode, isFloatInput, children }) => {
  const { applicationId } = useContext(ManufacturerContext);
  const [open, setOpen] = useState(false);
  const [informationValue, setInformationValue] = useState<string | number>(informationCurrentValue ?? "");
  const { toast } = useToast();

  const [updateCategoryProperty, { isLoading: isUpdating }] = usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeFieldNameMutation();

  const handleEditInformationClicked = () => {
    setOpen(true);
  };

  const handleConfirm = async () => {
    setOpen(false);

    try {
      await updateCategoryProperty({
        routeManufacturerId: applicationId,
        categoryCode: categoryCode,
        fieldName: informationName,
        newValue: String(informationValue),
      }).unwrap();
      toast({ variant: "success", title: "Success", description: `Property ${informationName} was successfully updated.` });
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: `Property ${informationName} failed to update.` });
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const isNumericLabel = label ? NUMERIC_VALUE_LABELS.has(label) : false;

  const handleInformationChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (isNumericLabel) {
      if (validateFonction(value) || value === "") {
        setInformationValue(value === "" ? "" : parseFloat(value));
      }
    } else {
      setInformationValue(value);
    }
  };

  return (
    <>
      <div onClick={handleEditInformationClicked} className="cursor-pointer">
        {children}
      </div>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {label || informationName}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2 py-4">
            <Label htmlFor={informationName}>
              {label || informationName}
            </Label>
            <Input
              id={informationName}
              autoFocus
              value={informationValue}
              onChange={handleInformationChanged}
              type={isFloatInput ? "number" : "text"}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isUpdating || !validateFonction(informationValue) || String(informationCurrentValue) === String(informationValue)}
            >
              {isUpdating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating...</> : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditCategoryWrapper;
